<template>
    <fieldset aria-describedby="content-info-text">
        <legend
            v-if="listHeading"
            :id="uniqueId"
            class="legendelement"
        >
            {{ $t(listHeading) }}
        </legend>
        <div class="radiobuttonelement">
            <div
                :key="forceUpdateKey"
                role="radiogroup"
            >
                <div
                    v-for="(item, index) in elementsLoaded"
                    :key="index"
                    class="rb-ddl-item"
                >
                    <div class="radio-button-group">
                        <input
                            :id="item.radioValue + index"
                            ref="activeRadioButton"
                            type="radio"
                            :value="index"
                            :checked="item.labelText === selectedRadioButton"
                            :name="item.radioValue + index"
                            class=""
                            @change="changeRadioButton(item, $event)"
                        >
                        <label :for="item.radioValue + index">{{ $t(item.labelText) }}</label>
                    </div>
                    <div
                        v-show="item.dropDownListItems"
                        class="dropdown-group"
                    >
                        <dropdown
                            :disabled="item.labelText != selectedRadioButton"
                            :list="item.dropDownListItems"
                            :input-id="item.radioValue + '-select-' + index"
                            :placeholder="getPlaceholdertext(item.radioValue)"
                            :input-name="item.radioValue"
                            :selected-list-value="getSelectedDropDownListValue(item.radioValue)"
                            @valueselected="changeDropDown"
                        />
                    </div>
                </div>
            </div>
        </div>
    </fieldset>
</template>

<script lang="ts">
    import { Vue, Component, Prop, Watch } from "vue-facing-decorator"
    import RadioButtonModel from "@/models/radiobuttonmodel"
    import SelectListWithSearch from "@/components/SelectListWithSearch.vue"
    import DropDownListModel from "@/models/dropdownlistmodel"

    @Component({
        components: {
            dropdown: SelectListWithSearch
        }
    })
    /** RadiobuttonList dropdownilla -> käytetään vain kunta/maakunta komponentin kanssa */
    export default class RadioButtonListWithDropdownSearch extends Vue {
        selectedRadioButton: string = ""
        radiovalueMunicipality = "kunta"
        uniqueId: string = ""
        ticks: number = 0

        @Prop({ type: Array, default: [] }) radioButtonList!: Array<RadioButtonModel>
        @Prop() visibleItems: number | undefined
        @Prop() listHeading: string | undefined
        @Prop() defaultRadioButton: string | undefined

        forceUpdateKey = 0

        created() {
            this.uniqueId = "id-" + Math.random().toString(16).slice(2)
        }

        mounted(): void {
            this.selectedRadioButton = this.defaultRadioButton ? this.defaultRadioButton : ""
            this.emitRadioButtonSelectionChanged()
        }

        async activated(): Promise<void> {
            // Asetetaan valituksi storessa määritelty RadioButton
            let updated = false
            for (const model of this.radioButtonList) {
                if (model.radioValue === this.$store.getters.selectedAreaRadioButton) {
                    this.selectedRadioButton = model.labelText
                    updated = true
                    break
                }
            }

            if (updated === false) {
                // Tällä sivulla ei ole storessa valittua radiobuttonia (Esim. Viat ja häiriöt sivulla ei ole kuntavalintaa),
                // joten asetetaan listan ensimmäinen radiobutton valituksi.
                this.selectedRadioButton = this.radioButtonList[0].labelText
                this.$store.dispatch("setSelectedAreaRadioButton", this.radioButtonList[0].radioValue)
            }

            this.forceUpdateKey += 1 // Pakotetaan radiobuttoneiden päivitys, jotta oikea radiobutton näkyy varmasti valittuna
        }

        get elementsLoaded(): Array<RadioButtonModel> {
            return this.radioButtonList
        }

        get defaultRadioButtonChanged(): string | undefined {
            return this.defaultRadioButton
        }

        @Watch("defaultRadioButtonChanged")
        /** asetaan valituksi default radio button. Jos sillä ei ole arvoa, pitäydytään jo valitussa arvossa */
        onDefaultRadioButtonChanged() {
            this.selectedRadioButton = this.defaultRadioButton ?? this.selectedRadioButton
        }

        setChecked(item: RadioButtonModel): boolean {
            return item.labelText === this.selectedRadioButton
        }

        getPlaceholdertext(radiovalue: string): string {
            if (radiovalue === this.radiovalueMunicipality) return this.$t("App.TieLiikenne.MunicipalityListDefaultText") ?? ""
            return this.$t("App.TieLiikenne.ProvinceListDefaultText") ?? ""
        }

        getSelectedDropDownListValue(radiovalue: string): DropDownListModel | undefined {
            let value = this.$store.getters.selectedProvince ?? ""
            if (radiovalue === this.radiovalueMunicipality) value = this.$store.getters.selectedMunicipality ?? ""
            const radiobutton = this.radioButtonList.find(x => x.radioValue === radiovalue) ?? RadioButtonModel.createEmpty()
            return radiobutton.dropDownListItems?.find(x => x.identifier === value)
        }

        emitRadioButtonSelectionChanged(): void {
            if (this.selectedRadioButton !== "") {
                // Laukaistaan default radio buttonin valinta. Tämä emitoidaan osiosivuille, jotka sitten hoitavat datan lataamisen.
                for (const model of this.elementsLoaded) {
                    if (model.labelText === this.selectedRadioButton) {
                        this.changeRadioButton(model, null)
                        break
                    }
                }
            }
        }

        changeRadioButton(item: RadioButtonModel, event): void {
            this.selectedRadioButton = item.labelText
            this.$emit("selectedRadioButton", item)
            this.setFocusToRadioButton(2, event)
        }

        changeDropDown(item): void {
            // siirretään radiobuttonin valinta kuntaa / maakuntaan sen mukaan mikä dropdown valinnan käyttäjä on tehnyt, jos tarvetta.
            const radiobutton = this.radioButtonList.find(x => x.radioValue === item.name) ?? RadioButtonModel.createEmpty()
            if (this.selectedRadioButton !== radiobutton.labelText) {
                this.selectedRadioButton = radiobutton.labelText
                this.emitRadioButtonSelectionChanged()
            }
            const dropdownItem = item.dropdownItem as DropDownListModel
            this.$emit("selectedDropDown", dropdownItem.identifier)
        }

        // sets focus to the selected radio button
        setFocusToRadioButton(ticksToWait, event) {
            if (this.ticks < ticksToWait) {
                this.ticks++

                this.$nextTick(() => {
                    this.setFocusToRadioButton(ticksToWait, event)
                })
            }
            else {
                this.ticks = 0

                if (event) {
                    event.target.focus()
                }
            }
        }
    }
</script>

<style scoped>
    .radiobuttonelement {
        margin-bottom: 0.5rem;
    }

    .legendelement {
        font-size: 1.125rem;
        font-family: Montserrat;
    }

    .rb-ddl-item {
        margin-bottom: 7px;
    }

    div.radio-button-group label {
        vertical-align: super;
        line-height: 22px;
    }

    .dropdownelement {
        margin: 0 0 12px 0;
    }

    .dropdownelement select.error {
        background-color: rgba(158,0,59,0.05);
    }

    .dropdown-group :deep(label.checkbox-select-list) {
        width: 100%;
    }
    .dropdown-group :deep(span.selectlist-content) {
        width: 100%;
    }
</style>
